import React from 'react'
import { Link } from 'react-router'
import type { SameAddressResult } from '../../services/apiClient'

/** From this many active neighbours the address is flagged (likely a shared mailbox). */
export const SAME_ADDRESS_FLAG_THRESHOLD = 10

interface SameAddressCompaniesProps {
  result: SameAddressResult | null
}

export function sameAddressCountLabel(result: Pick<SameAddressResult, 'totalCount' | 'activeCount'>): string {
  const others = result.totalCount === 1 ? '1 andet selskab' : `${result.totalCount} andre selskaber`
  if (result.activeCount === result.totalCount) return others
  return `${others}, ${result.activeCount} ${result.activeCount === 1 ? 'aktivt' : 'aktive'}`
}

/**
 * Short link after the address in the company meta line: »148 andre på
 * adressen«, pointing at the full list on the Forbindelser tab. Many
 * active neighbours are a red flag (DESIGN.md → Rødt-flag); the active
 * count lives in the tooltip. Renders nothing while loading or when the
 * company is alone at its address.
 */
const SameAddressCompanies: React.FC<SameAddressCompaniesProps> = ({ result }) => {
  if (!result || !result.address || result.totalCount === 0) return null

  const flagged = result.activeCount >= SAME_ADDRESS_FLAG_THRESHOLD
  const label = result.totalCount === 1 ? '1 anden på adressen' : `${result.totalCount} andre på adressen`

  return (
    <Link
      to={{ search: '?tab=forbindelser', hash: '#samme-adresse' }}
      title={`${sameAddressCountLabel(result)} på samme adresse. Se dem under Forbindelser.`}
      data-testid="company-same-address"
      className={`underline decoration-dotted underline-offset-[3px] transition-colors ${
        flagged
          ? 'font-semibold text-signal-700 decoration-signal-300 hover:decoration-signal-700'
          : 'text-ink-600 decoration-ink-300 hover:text-ink hover:decoration-ink'
      }`}
    >
      {label}
    </Link>
  )
}

export default SameAddressCompanies
